/**
 * The websocket bridge for the admin's live surfaces (plans.md §6).
 *
 * The admin is subscribed server-side to the `role:admin` topic, which carries:
 *
 *   queue.approvals     a producer submitted or re-submitted a profile
 *   queue.pricing       a product entered the pricing queue (new, or margin-guard unpublish)
 *   order.created       an RFQ landed
 *   notification.dead   an outbox row exhausted its retries — the loudest event in the system
 *
 * Rather than trusting the frame's own numbers, we invalidate the affected query keys and let
 * TanStack refetch. The websocket is a CACHE-INVALIDATION SIGNAL, not a second source of
 * truth: a frame that carried counters would be a second DTO of the same data, drifting from
 * the REST one, and the anonymity rule (one mapper per audience) is easier to hold when there
 * is exactly one shape per audience.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWsEvent, useWsStatus } from '@most/ws-client';
import { getConfig } from './api';
import { qk } from './queries';

/**
 * Does this server actually serve `GET /api/v1/ws`?
 *
 * The socket must be gated on the server SAYING SO, never on dialling it and interpreting the
 * failure — "this deployment has no live channel" and "the live channel just died" produce an
 * identical failed connect but demand opposite UI. The first is normal and should be silent
 * (we poll instead); the second is a warning that Merdan's queue counters have gone stale
 * behind his back.
 *
 * Undefined while the config is in flight, and treated as OFF until proven on: a brief silence
 * is the right failure mode for a warning indicator, whereas a warning that flashes on every
 * cold load is one Merdan learns to ignore.
 */
export function useLiveChannelEnabled(): boolean {
  const { data } = useQuery({
    queryKey: qk.config,
    queryFn: getConfig,
    staleTime: Infinity, // Server capabilities do not change under a running tab.
  });
  return data?.live_channel ?? false;
}

export function useAdminLiveUpdates(): void {
  const qc = useQueryClient();

  const invalidateDashboard = () => {
    void qc.invalidateQueries({ queryKey: qk.dashboard });
  };

  // Sidebar + ADM-1 counters.
  useWsEvent('queue.approvals', () => {
    invalidateDashboard();
    void qc.invalidateQueries({ queryKey: ['admin', 'producers'] });
  });

  useWsEvent('queue.pricing', () => {
    invalidateDashboard();
    void qc.invalidateQueries({ queryKey: ['admin', 'pricing-queue'] });
  });

  // ADM-4 board goes live.
  useWsEvent('order.created', () => {
    invalidateDashboard();
    void qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
  });

  // A dead admin alert means an order arrived and nobody was told. Badge it hard.
  useWsEvent('notification.dead', () => {
    invalidateDashboard();
    void qc.invalidateQueries({ queryKey: ['admin', 'notifications'] });
  });

  // Support chat. A frame carries only the changed message/conversation id; the inbox and
  // the open thread refetch through the admin mapper. (`['admin','chat']` prefixes every
  // per-thread message key.)
  useWsEvent('chat.message', () => {
    void qc.invalidateQueries({ queryKey: qk.chats });
    void qc.invalidateQueries({ queryKey: ['admin', 'chat'] });
  });
  useWsEvent('chat.read', () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'chat'] });
  });
}

export { useWsStatus };
